import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Lock, Globe2, Loader2, Clock, Crown } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

/**
 * GlobalCommunitiesTab — admin panel tab listing every community on the
 * platform with its member count, privacy, pending join-request count,
 * and last-message time. Ordered by member count desc.
 */
export default function GlobalCommunitiesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/communities/global");
        if (!cancelled) setRows(data.communities || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="text-sm text-zinc-500 py-12 text-center">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Loading communities…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="text-center text-sm text-zinc-500 py-16">
        No communities yet.
      </div>
    );
  }
  return (
    <div className="border border-white/10 rounded-md overflow-hidden" data-testid="admin-global-communities-tab">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-zinc-400">
          <tr>
            <th className="text-left px-4 py-3">Community</th>
            <th className="text-left px-4 py-3">Owner</th>
            <th className="text-right px-4 py-3">Members</th>
            <th className="text-right px-4 py-3">Pending</th>
            <th className="text-right px-4 py-3">Last activity</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((c) => (
            <tr key={c.id} data-testid={`admin-community-row-${c.id}`} className="bg-zinc-950/40 hover:bg-zinc-950/70">
              <td className="px-4 py-3">
                <Link to={`/c/${c.id}`} className="flex items-center gap-2 hover:text-rose-300">
                  {c.visibility === "private" ? (
                    <Lock size={12} className="text-rose-400" />
                  ) : (
                    <Globe2 size={12} className="text-emerald-400" />
                  )}
                  <span className="text-white truncate">{c.name}</span>
                </Link>
              </td>
              <td className="px-4 py-3">
                {c.owner ? (
                  <Link to={`/u/${c.owner.id}`} className="flex items-center gap-2 hover:text-rose-300">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={c.owner.avatar_url} />
                      <AvatarFallback className="bg-rose-600 text-white text-[10px]">
                        {c.owner.handle?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex items-center gap-1">
                      <Crown size={10} className="text-amber-400" />
                      <span className="text-xs text-zinc-300 truncate">{c.owner.display_name}</span>
                    </div>
                  </Link>
                ) : (
                  <span className="text-xs text-zinc-500">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <span className="inline-flex items-center gap-1 text-xs text-white">
                  <Users size={11} /> {(c.members_count || 0).toLocaleString()}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                {c.pending_requests > 0 ? (
                  <span
                    data-testid={`admin-community-${c.id}-pending`}
                    className="inline-flex items-center gap-1 text-xs bg-amber-500/10 border border-amber-400/30 text-amber-300 rounded-full px-2 py-0.5"
                  >
                    <Clock size={10} /> {c.pending_requests}
                  </span>
                ) : (
                  <span className="text-xs text-zinc-600">0</span>
                )}
              </td>
              <td className="px-4 py-3 text-right text-xs text-zinc-400">
                {c.last_message_at
                  ? new Date(c.last_message_at).toLocaleString()
                  : <span className="text-zinc-600">Never</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
